'use client';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import { TransactionStatus } from '@/models/transaction';
import { Box, Grid, useTheme } from '@mui/material';
import {
    TextDisplayMDSemiBold,
    TextMDSemiBold,
    TextSMSemiBold
} from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { HistoryStatus } from './HistoryStatus';
import { SecondaryButtonGrayLG } from '@/components/shared/Button';
import { useFormatting } from '@/utils/formatting';
import { DATE_FORMAT } from '@/lib/constants';
import { useTransactionModalStore } from './HistoryStore';
import { NormalGrid, HeaderGrid } from './common';

export const TransactionDetailDialog = () => {
    const isOpen = useTransactionModalStore((state) => state.isOpen);
    const closeModal = useTransactionModalStore((state) => state.closeModal);
    const content = useTransactionModalStore((state) => state.content);

    const { t } = useTranslation();
    const theme = useTheme();

    const { getBalanceLabel } = useFormatting();

    return (
        <StyledDialog open={isOpen}>
            <StyledDialogContainer
                style={{
                    width: '480px',
                    padding: '24px'
                }}
            >
                <Box textAlign={'center'}>
                    <TextMDSemiBold mb={1}>
                        {t('transaction_details')}
                    </TextMDSemiBold>
                    <TextDisplayMDSemiBold>
                        {getBalanceLabel(
                            content?.amount,
                            content?.currency ?? 'DKK',
                            undefined,
                            true
                        )}
                    </TextDisplayMDSemiBold>
                </Box>

                <Grid mb={3} container spacing={2} mt={2}>
                    <NormalGrid size={6}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('status')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid
                        size={6}
                        display={'flex'}
                        justifyContent={'end'}
                    >
                        <HistoryStatus
                            status={content?.status as TransactionStatus}
                        />
                    </NormalGrid>
                    {content?.transactionType && (
                        <>
                            <NormalGrid size={6}>
                                <TextSMSemiBold
                                    color={theme.palette.text.muted}
                                >
                                    {t('type')}
                                </TextSMSemiBold>
                            </NormalGrid>
                            <NormalGrid
                                size={6}
                                display={'flex'}
                                justifyContent={'end'}
                            >
                                <TextSMSemiBold fontWeight={700}>
                                    {content?.transactionType}
                                </TextSMSemiBold>
                            </NormalGrid>
                        </>
                    )}
                    <NormalGrid size={6}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('Creation date')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid
                        size={6}
                        display={'flex'}
                        justifyContent={'end'}
                    >
                        <TextSMSemiBold>
                            {content?.createdAt?.format(
                                DATE_FORMAT.FULL_DATE_TIME
                            )}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid size={6}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('Finalised date')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid
                        size={6}
                        display={'flex'}
                        justifyContent={'end'}
                    >
                        <TextSMSemiBold>
                            {content?.performedAt
                                ? content?.performedAt?.format(
                                      DATE_FORMAT.FULL_DATE_TIME
                                  )
                                : 'Not finalised'}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid size={6}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('Approval Type')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid
                        size={6}
                        display={'flex'}
                        justifyContent={'end'}
                    >
                        <TextSMSemiBold>{content?.approvalType}</TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid size={6}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('Approved by')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid
                        size={6}
                        display={'flex'}
                        justifyContent={'end'}
                    >
                        <TextSMSemiBold>
                            {content?.executantAdminName ?? 'Not approved'}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <HeaderGrid
                        size={12}
                        display={'flex'}
                        justifyContent={'start'}
                    >
                        <TextSMSemiBold>
                            {t('Recipient details')}
                        </TextSMSemiBold>
                    </HeaderGrid>
                    <NormalGrid size={6}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('Recipient Name')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid
                        size={6}
                        display={'flex'}
                        justifyContent={'end'}
                    >
                        <TextSMSemiBold fontWeight={700}>
                            {content?.userName}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid size={4}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('User ID')}
                        </TextSMSemiBold>
                    </NormalGrid>

                    <NormalGrid p={0} size={8} textAlign={'right'}>
                        <TextSMSemiBold>{content?.userId}</TextSMSemiBold>
                    </NormalGrid>

                    <HeaderGrid
                        size={12}
                        display={'flex'}
                        justifyContent={'start'}
                    >
                        <TextSMSemiBold>{t('Company details')}</TextSMSemiBold>
                    </HeaderGrid>
                    <NormalGrid size={4}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('Company name')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid p={0} size={8} textAlign={'right'}>
                        <TextSMSemiBold>
                            {content?.companyName ?? 'Not provided'}
                        </TextSMSemiBold>
                    </NormalGrid>

                    <NormalGrid size={4}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('Payment Source')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid p={0} size={8} textAlign={'right'}>
                        <TextSMSemiBold>
                            {content?.paymentSource ?? 'Not provided'}
                        </TextSMSemiBold>
                    </NormalGrid>

                    <NormalGrid size={4}>
                        <TextSMSemiBold color={theme.palette.text.muted}>
                            {t('Transaction ID')}
                        </TextSMSemiBold>
                    </NormalGrid>
                    <NormalGrid p={0} size={8} textAlign={'right'}>
                        <TextSMSemiBold>
                            {content?.transactionId}
                        </TextSMSemiBold>
                    </NormalGrid>
                </Grid>

                <SecondaryButtonGrayLG onClick={closeModal}>
                    {t('close')}
                </SecondaryButtonGrayLG>
            </StyledDialogContainer>
        </StyledDialog>
    );
};
